import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LocationAutocomplete from "../components/LocationAutocomplete";
import { TierBadge } from "./MyLeads";
import { refreshPlanAccess } from "../hooks/usePlanAccess";
import { openUpgrade } from "../components/UpgradeDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Session storage key for persisting search results
const DISCOVER_SESSION_KEY = 'discover_search_state';

function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Advanced Search is available to admins and paid plans (Starter/Growth/Scale)
  const [canUseAdvanced, setCanUseAdvanced] = useState(false);
  
  // Load initial state from sessionStorage
  const getInitialState = useCallback(() => {
    try {
      const saved = sessionStorage.getItem(DISCOVER_SESSION_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved search state:', e);
    }
    return null;
  }, []);

  const savedState = getInitialState();
  
  // Advanced Search toggle (Admin only, session-based)
  const [advancedSearchEnabled, setAdvancedSearchEnabled] = useState(savedState?.advancedSearchEnabled || false);
  const [searchMode, setSearchMode] = useState(savedState?.searchMode || "filters"); // "filters" or "template"
  
  // Standard Hunter.io states
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(savedState?.results || []);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [scanning, setScanning] = useState(false);

  // Standard filter states (Hunter.io)
  const [selectedIndustry, setSelectedIndustry] = useState(savedState?.selectedIndustry || "");
  const [selectedHeadcount, setSelectedHeadcount] = useState(savedState?.selectedHeadcount || "");
  const [selectedCompanyType, setSelectedCompanyType] = useState(savedState?.selectedCompanyType || "");
  
  // Location filter states (shared)
  const [useLocation, setUseLocation] = useState(savedState?.useLocation || false);
  const [selectedCountry, setSelectedCountry] = useState(savedState?.selectedCountry || "US");
  const [selectedState, setSelectedState] = useState(savedState?.selectedState || "");
  const [selectedCity, setSelectedCity] = useState(savedState?.selectedCity || "");

  // Apollo.io specific states (Advanced Search)
  const [apolloFilters, setApolloFilters] = useState({ seniorities: [], industries: [] });
  const [apolloJobTitle, setApolloJobTitle] = useState(savedState?.apolloJobTitle || "");
  const [apolloSeniority, setApolloSeniority] = useState(savedState?.apolloSeniority || "");
  const [apolloIndustry, setApolloIndustry] = useState(savedState?.apolloIndustry || "");
  const [apolloPersonLocation, setApolloPersonLocation] = useState(savedState?.apolloPersonLocation || "");
  const [apolloCompanyLocation, setApolloCompanyLocation] = useState(savedState?.apolloCompanyLocation || "");
  const [apolloEmployeeMin, setApolloEmployeeMin] = useState(savedState?.apolloEmployeeMin || "");
  const [apolloEmployeeMax, setApolloEmployeeMax] = useState(savedState?.apolloEmployeeMax || "");
  const [apolloKeywords, setApolloKeywords] = useState(savedState?.apolloKeywords || "");
  const [apolloAudienceName, setApolloAudienceName] = useState(savedState?.apolloAudienceName || "");
  
  // Apollo results specific
  const [apolloResults, setApolloResults] = useState(savedState?.apolloResults || []);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [autoPhonesAlways, setAutoPhonesAlways] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  const [enriching, setEnriching] = useState(false);
  
  // Pagination state for Apollo results
  const [apolloCurrentPage, setApolloCurrentPage] = useState(savedState?.apolloCurrentPage || 1);
  const [apolloPageSize, setApolloPageSize] = useState(savedState?.apolloPageSize || 25);
  const [apolloTotalRecords, setApolloTotalRecords] = useState(savedState?.apolloTotalRecords || 0);
  const [apolloTotalPages, setApolloTotalPages] = useState(savedState?.apolloTotalPages || 1);
  
  // Availability counts (like Hunter.io)
  const [emailsAvailable, setEmailsAvailable] = useState(0);
  const [phonesAvailable, setPhonesAvailable] = useState(0);
  
  // Store current search params for pagination
  const [lastSearchParams, setLastSearchParams] = useState(savedState?.lastSearchParams || null);

  // Save state to sessionStorage when important values change
  useEffect(() => {
    const stateToSave = {
      advancedSearchEnabled,
      searchMode,
      results,
      selectedIndustry,
      selectedHeadcount,
      selectedCompanyType,
      useLocation,
      selectedCountry,
      selectedState,
      selectedCity,
      apolloJobTitle,
      apolloSeniority,
      apolloIndustry,
      apolloPersonLocation,
      apolloCompanyLocation,
      apolloEmployeeMin,
      apolloEmployeeMax,
      apolloKeywords,
      apolloAudienceName,
      apolloResults,
      apolloCurrentPage,
      apolloPageSize,
      apolloTotalRecords,
      apolloTotalPages,
      lastSearchParams
    };
    
    try {
      sessionStorage.setItem(DISCOVER_SESSION_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      console.error('Error saving search state:', e);
    }
  }, [
    advancedSearchEnabled, searchMode, results, selectedIndustry, selectedHeadcount,
    selectedCompanyType, useLocation, selectedCountry, selectedState, selectedCity,
    apolloJobTitle, apolloSeniority, apolloIndustry, apolloPersonLocation, apolloCompanyLocation,
    apolloEmployeeMin, apolloEmployeeMax, apolloKeywords, apolloAudienceName, apolloResults,
    apolloCurrentPage, apolloPageSize, apolloTotalRecords, apolloTotalPages, lastSearchParams
  ]);

  const headcountOptions = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "501-1000",
    "1001-5000",
    "5001-10000",
    "10001+"
  ];

  const companyTypeOptions = [
    "educational",
    "educational institution",
    "government agency",
    "non profit",
    "partnership",
    "privately held",
    "public company",
    "self employed",
    "self owned",
    "sole proprietorship"
  ];

  // US States
  const usStates = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" }
  ];

  const countries = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "GB", name: "United Kingdom" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" }
  ];

  useEffect(() => {
    loadIndustries();
    checkAdvancedAccess();
  }, []);

  const checkAdvancedAccess = async () => {
    try {
      const res = await axios.get(`${API}/subscription/limit-check`);
      const plan = (res.data?.plan || "free").toLowerCase();
      setCanUseAdvanced(res.data?.is_admin || ["starter", "growth", "scale"].includes(plan));
    } catch (e) {
      setCanUseAdvanced(false);
    }
  };

  // Load Apollo.io filters when advanced search is enabled
  useEffect(() => {
    if (advancedSearchEnabled && canUseAdvanced) {
      loadApolloFilters();
    }
  }, [advancedSearchEnabled, canUseAdvanced]);

  const loadIndustries = async () => {
    try {
      const response = await axios.get(`${API}/discover/industries`);
      setIndustries(response.data.industries || []);
    } catch (error) {
      console.error("Error loading industries:", error);
    }
  };

  const loadApolloFilters = async () => {
    try {
      const response = await axios.get(`${API}/apollo/filters`);
      setApolloFilters(response.data || { seniorities: [], industries: [] });
    } catch (error) {
      console.error("Error loading Apollo filters:", error);
      toast.error("Failed to load Advanced Search options");
    }
  };

  // Standard Hunter.io search
  const handleStandardSearch = async () => {
    if (!selectedIndustry && !selectedHeadcount && !selectedCompanyType && !useLocation) {
      toast.error("Please select at least one filter");
      return;
    }

    if (useLocation) {
      if (!selectedCity.trim()) {
        toast.error("City is required when using location filter");
        return;
      }
      if (selectedCountry === "US" && !selectedState) {
        toast.error("State is required for US locations");
        return;
      }
    }

    setLoading(true);
    setSelectedCompanies([]);

    try {
      const payload = {};
      
      if (selectedIndustry) {
        payload.industry = [selectedIndustry];
      }
      
      if (selectedHeadcount) {
        payload.headcount = [selectedHeadcount];
      }
      
      if (selectedCompanyType) {
        payload.company_type = [selectedCompanyType];
      }
      
      if (useLocation && selectedCity.trim()) {
        const locationFilter = {
          city: selectedCity.trim(),
          country: selectedCountry
        };
        
        if (selectedCountry === "US" && selectedState) {
          locationFilter.state = selectedState;
        }
        
        payload.headquarters_location = {
          include: [locationFilter]
        };
      }

      const response = await axios.post(`${API}/discover/search`, payload);
      const companies = response.data.data || [];
      setResults(companies);
      
      if (companies.length === 0) {
        toast.info("No companies found with these filters");
      } else {
        toast.success(`Found ${companies.length} companies!`);
      }
    } catch (error) {
      console.error("Error searching:", error);
      toast.error("Failed to search companies");
    } finally {
      setLoading(false);
    }
  };

  // Apollo.io Advanced Search
  const handleAdvancedSearch = async (page = 1) => {
    if (!apolloJobTitle && !apolloSeniority && !apolloIndustry && !apolloPersonLocation && !apolloCompanyLocation && !apolloKeywords) {
      toast.error("Please select at least one filter");
      return;
    }

    setLoading(true);
    if (page === 1) {
      setSelectedLeads([]);
    }

    try {
      const payload = {
        page: page,
        per_page: apolloPageSize
      };
      
      if (apolloJobTitle.trim()) {
        payload.person_titles = [apolloJobTitle.trim()];
      }
      
      if (apolloSeniority) {
        payload.person_seniorities = [apolloSeniority];
      }
      
      if (apolloIndustry) {
        payload.organization_industries = [apolloIndustry];
      }
      
      if (apolloPersonLocation.trim()) {
        payload.person_locations = [apolloPersonLocation.trim()];
      }
      
      if (apolloCompanyLocation.trim()) {
        payload.organization_locations = [apolloCompanyLocation.trim()];
      }
      
      if (apolloEmployeeMin) {
        payload.employee_count_min = parseInt(apolloEmployeeMin);
      }
      
      if (apolloEmployeeMax) {
        payload.employee_count_max = parseInt(apolloEmployeeMax);
      }
      
      if (apolloKeywords.trim()) {
        payload.keywords = apolloKeywords.trim();
      }

      // Store search params for pagination
      setLastSearchParams(payload);

      const response = await axios.post(`${API}/apollo/search`, payload);
      const leads = response.data.results || [];
      setApolloResults(leads);
      setApolloCurrentPage(response.data.page || 1);
      setApolloTotalRecords(response.data.total_records || 0);
      setApolloTotalPages(response.data.total_pages || 1);
      setEmailsAvailable(response.data.emails_available || 0);
      setPhonesAvailable(response.data.phones_available || 0);
      
      if (leads.length === 0 && page === 1) {
        toast.info("No leads found with these filters");
      } else if (page === 1) {
        const emailCount = leads.filter(leadHasEmail).length;
        const phoneCount = leads.filter(leadHasPhone).length;
        toast.success(
          <div>
            <p className="font-semibold">Found {response.data.total_records} leads!</p>
            <p className="text-sm">📧 {emailCount} with email • 📞 {phoneCount} with phone</p>
          </div>,
          { duration: 4000 }
        );
      }
    } catch (error) {
      console.error("Error searching:", error);
      const errorMsg = error.response?.data?.detail || "Failed to search";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (advancedSearchEnabled) {
      handleAdvancedSearch();
    } else {
      handleStandardSearch();
    }
  };

  // Standard scan handlers
  const handleSelectCompany = (domain) => {
    setSelectedCompanies(prev => {
      if (prev.includes(domain)) {
        return prev.filter(d => d !== domain);
      } else {
        return [...prev, domain];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCompanies.length === results.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(results.map(r => r.domain));
    }
  };

  // Apollo lead handlers
  const handleSelectLead = (index) => {
    setSelectedLeads(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectAllLeads = () => {
    if (selectedLeads.length === apolloResults.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(apolloResults.map((_, i) => i));
    }
  };

  const handleViewMore = (lead) => {
    setSelectedLeadDetails(lead);
    setShowLeadModal(true);
  };

  // Extract a displayable phone string from an Apollo phone_number object
  const getPhoneDisplay = (pn) => {
    if (!pn) return '';
    if (typeof pn === 'string') return pn;
    return pn.sanitized_number || pn.raw_number || pn.number || pn.phone_number || '';
  };

  // Collect all available emails for a lead (work + personal)
  const getAllEmails = (lead) => {
    const emails = [];
    const seen = new Set();
    const pushEmail = (val, status, type) => {
      if (!val || seen.has(val.toLowerCase())) return;
      seen.add(val.toLowerCase());
      emails.push({ email: val, status, type });
    };
    if (lead?.email) pushEmail(lead.email, lead.email_status, 'Work');
    (lead?.personal_emails || []).forEach((e) => {
      const val = typeof e === 'string' ? e : (e?.email || e?.value);
      const status = typeof e === 'object' ? (e?.email_status || e?.status) : undefined;
      pushEmail(val, status, 'Personal');
    });
    return emails;
  };

  // Collect all available phone numbers for a lead
  const getAllPhones = (lead) => {
    const phones = [];
    const seen = new Set();
    (lead?.phone_numbers || []).forEach((pn) => {
      const val = getPhoneDisplay(pn);
      if (!val || seen.has(val)) return;
      seen.add(val);
      phones.push({
        number: val,
        type: (typeof pn === 'object' ? (pn.type_cd || pn.type) : null) || null,
        status: (typeof pn === 'object' ? (pn.status_cd || pn.status) : null) || null,
      });
    });
    if (phones.length === 0 && lead?.phone && !seen.has(lead.phone)) {
      phones.push({ number: lead.phone, type: null, status: null });
    }
    return phones;
  };

  // Top-of-results counts, derived from the SAME data the table columns use so they always match.
  const leadHasEmail = (l) => getAllEmails(l).length > 0 || l?._email_pending || l?.has_email;
  const leadHasPhone = (l) => getAllPhones(l).length > 0 || l?._phone_pending || l?.has_phone;
  const emailsCount = apolloResults.filter(leadHasEmail).length;
  const phonesCount = apolloResults.filter(leadHasPhone).length;

  // Fetch a contact's complete profile via Apollo Get Complete Person Info (uses credits)
  const [loadingCompleteInfo, setLoadingCompleteInfo] = useState(false);

  const handleGetCompleteInfo = async (lead) => {
    const personId = lead?._raw?.id;
    if (!personId) {
      toast.error("No contact ID available for this lead");
      return;
    }
    if (!window.confirm("Fetching complete info uses 1 credit. Continue?")) return;

    setLoadingCompleteInfo(true);
    try {
      const response = await axios.post(`${API}/apollo/person-info`, { person_id: personId });
      const profile = response.data.person || {};
      const updatedResults = apolloResults.map((l) => {
        if (l?._raw?.id !== personId) return l;
        const merged = { ...l };
        Object.keys(profile).forEach((k) => {
          const val = profile[k];
          if (val === null || val === undefined) return;
          if (Array.isArray(val) && val.length === 0) return;
          if (k === 'email' && !val) return; // never wipe a revealed email
          merged[k] = val;
        });
        merged._complete_info = true;
        return merged;
      });
      setApolloResults(updatedResults);
      const refreshed = updatedResults.find((l) => l?._raw?.id === personId);
      if (refreshed) setSelectedLeadDetails(refreshed);
      toast.success("Complete info loaded!");
    } catch (error) {
      console.error("Error fetching complete info:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch complete info");
    } finally {
      setLoadingCompleteInfo(false);
    }
  };

  // Pagination handlers for Apollo results
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > apolloTotalPages || loading) return;
    
    setSelectedLeads([]); // Clear selections on page change
    handleAdvancedSearchPage(newPage);
  };

  const handleAdvancedSearchPage = async (page) => {
    if (!lastSearchParams) return;
    
    setLoading(true);
    try {
      const payload = { ...lastSearchParams, page, per_page: apolloPageSize };
      const response = await axios.post(`${API}/apollo/search`, payload);
      const leads = response.data.results || [];
      setApolloResults(leads);
      setApolloCurrentPage(response.data.page || 1);
      setApolloTotalRecords(response.data.total_records || 0);
      setApolloTotalPages(response.data.total_pages || 1);
      setEmailsAvailable(response.data.emails_available || 0);
      setPhonesAvailable(response.data.phones_available || 0);
    } catch (error) {
      console.error("Error fetching page:", error);
      toast.error("Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setApolloPageSize(parseInt(newSize));
    setApolloCurrentPage(1);
    setSelectedLeads([]);
    
    // Re-fetch with new page size
    if (lastSearchParams) {
      setTimeout(() => {
        const payload = { ...lastSearchParams, page: 1, per_page: parseInt(newSize) };
        setLastSearchParams(payload);
        handleAdvancedSearchPage(1);
      }, 0);
    }
  };

  // Enrich selected leads with full contact data
  const handleEnrichSelected = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Please select leads to enrich");
      return;
    }
    
    if (selectedLeads.length > 10) {
      toast.error("Maximum 10 leads can be enriched at once");
      return;
    }
    
    // Get the Apollo IDs from selected leads that have emails available but not yet revealed
    const leadsWithEmails = selectedLeads
      .map(i => apolloResults[i])
      .filter(lead => lead?.has_email && !lead?.email);
    
    if (leadsWithEmails.length === 0) {
      toast.info("Selected leads either already have emails or none available");
      return;
    }
    
    const personIds = leadsWithEmails
      .map(lead => lead?._raw?.id)
      .filter(Boolean);
    
    if (personIds.length === 0) {
      toast.error("No valid lead IDs found");
      return;
    }
    
    setEnriching(true);
    try {
      const response = await axios.post(`${API}/apollo/enrich`, {
        person_ids: personIds,
        reveal_emails: true,
        reveal_phones: false
      });
      
      const enrichedResults = response.data.results || [];
      
      // Update the apolloResults with enriched data
      const updatedResults = [...apolloResults];
      enrichedResults.forEach(enriched => {
        const idx = selectedLeads.find(i => 
          apolloResults[i]?._raw?.id === enriched.id
        );
        if (idx !== undefined && updatedResults[idx]) {
          updatedResults[idx] = {
            ...updatedResults[idx],
            email: enriched.email || updatedResults[idx].email,
            email_status: enriched.email_status || updatedResults[idx].email_status,
            phone: enriched.phone || updatedResults[idx].phone,
            phone_numbers: enriched.phone_numbers || updatedResults[idx].phone_numbers,
            personal_emails: enriched.personal_emails,
            // Merge richer profile fields returned by bulk_match
            first_name: enriched.first_name || updatedResults[idx].first_name,
            last_name: enriched.last_name || updatedResults[idx].last_name,
            job_title: enriched.job_title || updatedResults[idx].job_title,
            seniority: enriched.seniority || updatedResults[idx].seniority,
            linkedin_url: enriched.linkedin_url || updatedResults[idx].linkedin_url,
            city: enriched.city || updatedResults[idx].city,
            state: enriched.state || updatedResults[idx].state,
            country: enriched.country || updatedResults[idx].country,
            company: enriched.company || updatedResults[idx].company,
            company_domain: enriched.company_domain || updatedResults[idx].company_domain,
            company_industry: enriched.company_industry || updatedResults[idx].company_industry,
            company_phone: enriched.company_phone || updatedResults[idx].company_phone,
            _enriched: true
          };
        }
      });
      
      setApolloResults(updatedResults);
      toast.success(`Enriched ${enrichedResults.length} leads! (${response.data.credits_used} credits used)`);
    } catch (error) {
      console.error("Error enriching leads:", error);
      const errorMsg = error.response?.data?.detail || "Failed to enrich leads";
      toast.error(errorMsg);
    } finally {
      setEnriching(false);
    }
  };

  // Enrich phones for selected leads (costs 8 credits per phone)
  const [enrichingPhones, setEnrichingPhones] = useState(false);
  
  const handleEnrichPhones = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Please select leads to enrich");
      return;
    }
    
    if (selectedLeads.length > 10) {
      toast.error("Maximum 10 leads can be enriched at once");
      return;
    }
    
    // Get the Apollo IDs from selected leads that have phones available
    const leadsWithPhones = selectedLeads
      .map(i => apolloResults[i])
      .filter(lead => lead?.has_phone && !lead?.phone);
    
    if (leadsWithPhones.length === 0) {
      toast.info("Selected leads either already have phone numbers or none available");
      return;
    }
    
    const personIds = leadsWithPhones
      .map(lead => lead?._raw?.id)
      .filter(Boolean);
    
    const creditCost = personIds.length * 8;
    
    // Confirm the cost
    if (!window.confirm(`This will use ${creditCost} credits (8 per phone). Continue?`)) {
      return;
    }
    
    setEnrichingPhones(true);
    try {
      const response = await axios.post(`${API}/apollo/enrich`, {
        person_ids: personIds,
        reveal_emails: false,
        reveal_phones: true
      });
      
      if (response.data.phone_enrichment_pending) {
        toast.success(
          <div>
            <p className="font-semibold">Phone enrichment requested!</p>
            <p className="text-sm">{response.data.phone_credits} credits used</p>
            <p className="text-sm text-amber-600">Phone numbers may take several minutes — they'll appear automatically, and are also saved to My Leads.</p>
          </div>,
          { duration: 6000 }
        );
        
        // Mark leads as pending phone enrichment and store their IDs for later refresh
        const updatedResults = [...apolloResults];
        const pendingIds = [];
        selectedLeads.forEach(idx => {
          if (updatedResults[idx]) {
            const personId = updatedResults[idx]._raw?.id;
            if (personId) pendingIds.push({ idx, personId });
            updatedResults[idx] = {
              ...updatedResults[idx],
              _phone_pending: true,
              _phone_request_time: Date.now()
            };
          }
        });
        setApolloResults(updatedResults);
        
        // Store pending IDs for refresh
        sessionStorage.setItem('pending_phone_enrichments', JSON.stringify(pendingIds));
        
        // Start auto-refresh countdown
        startPhoneRefreshTimer();
      }
    } catch (error) {
      console.error("Error enriching phones:", error);
      const errorMsg = error.response?.data?.detail || "Failed to enrich phones";
      toast.error(errorMsg);
    } finally {
      setEnrichingPhones(false);
    }
  };

  // Auto-refresh timer for phone enrichments
  const [phoneRefreshCountdown, setPhoneRefreshCountdown] = useState(0);
  const [phoneRefreshAttempts, setPhoneRefreshAttempts] = useState(0);
  const maxRefreshAttempts = 30; // ~15 min total (Apollo phone reveal is a slow async queue)
  const refreshIntervalSeconds = 30;
  const [shouldCheckPhones, setShouldCheckPhones] = useState(false);
  
  const startPhoneRefreshTimer = () => {
    setPhoneRefreshCountdown(refreshIntervalSeconds);
    setPhoneRefreshAttempts(0);
  };
  
  // Countdown timer effect
  useEffect(() => {
    if (phoneRefreshCountdown <= 0) return;
    
    const timer = setInterval(() => {
      setPhoneRefreshCountdown(prev => {
        if (prev <= 1) {
          // Trigger phone check
          setShouldCheckPhones(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [phoneRefreshCountdown]);
  
  // Effect to check phones when triggered
  useEffect(() => {
    if (!shouldCheckPhones) return;
    setShouldCheckPhones(false);
    checkPhonesAutomatically();
  }, [shouldCheckPhones]);
  
  // Auto-check for phones when countdown reaches 0
  const checkPhonesAutomatically = async () => {
    const pendingData = sessionStorage.getItem('pending_phone_enrichments');
    if (!pendingData) return;
    
    const pendingIds = JSON.parse(pendingData);
    if (pendingIds.length === 0) return;
    
    setPhoneRefreshAttempts(prev => {
      if (prev >= maxRefreshAttempts) {
        toast.info("Max refresh attempts reached. Click 'Refresh Phones' manually to keep checking.");
        return prev;
      }
      return prev + 1;
    });
    
    setRefreshingPhones(true);
    let phonesFound = 0;
    
    try {
      const updatedResults = [...apolloResults];
      const stillPending = [];
      
      for (const { idx, personId } of pendingIds) {
        try {
          const response = await axios.get(`${API}/apollo/phones/${personId}`);
          if (response.data.found && response.data.phone_numbers?.length > 0) {
            const phoneNum = response.data.phone_numbers[0];
            const phone = phoneNum.sanitized_number || phoneNum.raw_number || phoneNum.number || phoneNum.phone_number;
            
            if (phone && updatedResults[idx]) {
              updatedResults[idx] = {
                ...updatedResults[idx],
                phone: phone,
                phone_numbers: response.data.phone_numbers,
                _phone_pending: false
              };
              phonesFound++;
            } else {
              stillPending.push({ idx, personId });
            }
          } else {
            stillPending.push({ idx, personId });
          }
        } catch (error) {
          console.error(`Error checking phone for ${personId}:`, error);
          stillPending.push({ idx, personId });
        }
      }
      
      setApolloResults(updatedResults);
      
      if (phonesFound > 0) {
        toast.success(`Found ${phonesFound} phone number(s)!`);
      }
      
      // Update pending list and continue checking if needed
      sessionStorage.setItem('pending_phone_enrichments', JSON.stringify(stillPending));
      
      if (stillPending.length > 0 && phoneRefreshAttempts < maxRefreshAttempts) {
        // Continue checking - restart countdown
        setPhoneRefreshCountdown(refreshIntervalSeconds);
      } else if (stillPending.length === 0) {
        // All phones found
        setPhoneRefreshCountdown(0);
        setPhoneRefreshAttempts(0);
        if (phonesFound === 0) {
          toast.info("All phone requests completed.");
        }
      }
    } catch (error) {
      console.error("Error auto-refreshing phones:", error);
      // Continue checking on error if not max attempts
      if (phoneRefreshAttempts < maxRefreshAttempts) {
        setPhoneRefreshCountdown(refreshIntervalSeconds);
      }
    } finally {
      setRefreshingPhones(false);
    }
  };

  // Check for phone enrichment results from webhook (manual refresh)
  const [refreshingPhones, setRefreshingPhones] = useState(false);
  
  const handleRefreshPhones = async () => {
    const pendingData = sessionStorage.getItem('pending_phone_enrichments');
    if (!pendingData) {
      toast.info("No pending phone enrichments to check");
      return;
    }
    
    const pendingIds = JSON.parse(pendingData);
    if (pendingIds.length === 0) {
      toast.info("No pending phone enrichments to check");
      return;
    }
    
    setRefreshingPhones(true);
    let phonesFound = 0;
    
    try {
      const updatedResults = [...apolloResults];
      
      for (const { idx, personId } of pendingIds) {
        try {
          const response = await axios.get(`${API}/apollo/phones/${personId}`);
          if (response.data.found && response.data.phone_numbers?.length > 0) {
            const phoneNum = response.data.phone_numbers[0];
            const phone = phoneNum.sanitized_number || phoneNum.raw_number || phoneNum.number || phoneNum.phone_number;
            
            if (phone && updatedResults[idx]) {
              updatedResults[idx] = {
                ...updatedResults[idx],
                phone: phone,
                phone_numbers: response.data.phone_numbers,
                _phone_pending: false
              };
              phonesFound++;
            }
          }
        } catch (error) {
          console.error(`Error checking phone for ${personId}:`, error);
        }
      }
      
      setApolloResults(updatedResults);
      
      if (phonesFound > 0) {
        toast.success(`Found ${phonesFound} phone number(s)!`);
        // Remove found entries from pending
        const stillPending = pendingIds.filter(({ idx }) => updatedResults[idx]?._phone_pending);
        sessionStorage.setItem('pending_phone_enrichments', JSON.stringify(stillPending));
      } else {
        toast.info("Phone numbers not yet available. Still processing - try again in a minute.");
      }
    } catch (error) {
      console.error("Error refreshing phones:", error);
      toast.error("Failed to check for phone numbers");
    } finally {
      setRefreshingPhones(false);
    }
  };

  // Start scan handlers
  const handleStartScan = async () => {
    if (advancedSearchEnabled) {
      await handleStartAdvancedScan();
    } else {
      await handleStartStandardScan();
    }
  };

  const handleStartStandardScan = async () => {
    if (selectedCompanies.length === 0) {
      toast.error("Please select at least one company");
      return;
    }

    try {
      const limitResponse = await axios.get(`${API}/subscription/limit-check`);
      const limitInfo = limitResponse.data;
      
      if (!limitInfo.is_admin && !limitInfo.can_create_lead) {
        toast.error(
          <div>
            <p className="font-semibold">Lead Limit Reached</p>
            <p className="text-sm">You've used {limitInfo.leads_used}/{limitInfo.leads_limit} leads this month.</p>
            <button
              onClick={() => navigate('/plans')}
              className="text-blue-600 underline text-sm mt-1"
            >
              Upgrade your plan →
            </button>
          </div>,
          { duration: 8000 }
        );
        return;
      }
    } catch (error) {
      console.error("Error checking lead limit:", error);
    }

    setScanning(true);
    try {
      const response = await axios.post(`${API}/discover/scan`, {
        domains: selectedCompanies
      });
      refreshPlanAccess().then((d) => {
        if (d?.plan === "free" && d.discover_trial?.remaining === 0) openUpgrade("discover_last");
      });
      
      toast.success(
        <div>
          <p className="font-semibold">Discover scan started!</p>
          <p className="text-sm">{selectedCompanies.length} companies • ID: {response.data.scan_id}</p>
          <button
            onClick={() => navigate('/scans')}
            className="text-blue-600 underline text-sm mt-1"
          >
            View Scan History →
          </button>
        </div>,
        { duration: 5000 }
      );
      
      setSelectedCompanies([]);
    } catch (error) {
      console.error("Error starting scan:", error);
      toast.error(error.response?.data?.detail || "Failed to start scan");
    } finally {
      setScanning(false);
    }
  };

  const handleStartAdvancedScan = () => {
    if (selectedLeads.length === 0) {
      toast.error("Please select at least one lead");
      return;
    }
    // If the user previously chose to always add phones, skip the prompt
    if (localStorage.getItem('revo_auto_enrich_phones') === 'true') {
      runAdvancedScan(true);
    } else {
      setAutoPhonesAlways(false);
      setShowPhonePrompt(true);
    }
  };

  const runAdvancedScan = async (enrichPhones) => {
    setShowPhonePrompt(false);
    setScanning(true);
    try {
      const leadsToScan = selectedLeads.map(i => apolloResults[i]);
      
      const response = await axios.post(`${API}/apollo/scan`, {
        leads: leadsToScan,
        audience_name: apolloAudienceName || "AdvancedSearch",
        enrich_phones: enrichPhones
      });
      
      toast.success(
        <div>
          <p className="font-semibold">Advanced scan started!</p>
          <p className="text-sm">
            {response.data.lead_count} leads • emails + CRM sync{enrichPhones ? ' + phone numbers' : ''}
          </p>
          <button
            onClick={() => navigate('/scans')}
            className="text-blue-600 underline text-sm mt-1"
          >
            View Scan History →
          </button>
        </div>,
        { duration: 5000 }
      );
      
      setSelectedLeads([]);
    } catch (error) {
      console.error("Error starting scan:", error);
      toast.error(error.response?.data?.detail || "Failed to start scan");
    } finally {
      setScanning(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedIndustry("");
    setSelectedHeadcount("");
    setSelectedCompanyType("");
    setUseLocation(false);
    setSelectedCountry("US");
    setSelectedState("");
    setSelectedCity("");
    // Apollo filters
    setApolloJobTitle("");
    setApolloSeniority("");
    setApolloIndustry("");
    setApolloPersonLocation("");
    setApolloCompanyLocation("");
    setApolloEmployeeMin("");
    setApolloEmployeeMax("");
    setApolloKeywords("");
    setApolloAudienceName("");
    setSearchMode("filters");
    setResults([]);
    setApolloResults([]);
    setSelectedCompanies([]);
    setSelectedLeads([]);
    // Reset pagination
    setApolloCurrentPage(1);
    setApolloTotalRecords(0);
    setApolloTotalPages(1);
    setLastSearchParams(null);
    // Clear session storage
    sessionStorage.removeItem(DISCOVER_SESSION_KEY);
  };

  return (
    <div data-testid="discover-page">
      <div className="page-header">
        <h1 className="page-title">Discover Companies</h1>
        <p className="page-description">
          Search for companies using advanced filters and start targeted scans
        </p>
      </div>

      {/* Advanced Search Toggle - Admins + Paid plans */}
      {canUseAdvanced && (
        <div className="content-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: advancedSearchEnabled ? '#7c3aed' : '#64748b'
              }}>
                <input
                  type="checkbox"
                  checked={advancedSearchEnabled}
                  onChange={(e) => {
                    setAdvancedSearchEnabled(e.target.checked);
                    // Clear Apollo filters
                    setApolloJobTitle("");
                    setApolloSeniority("");
                    setApolloIndustry("");
                    setApolloPersonLocation("");
                    setApolloCompanyLocation("");
                    setApolloEmployeeMin("");
                    setApolloEmployeeMax("");
                    setApolloKeywords("");
                    setApolloAudienceName("");
                    setSearchMode("filters");
                    // Clear selections but keep results visible
                    setSelectedCompanies([]);
                    setSelectedLeads([]);
                  }}
                  style={{ 
                    width: '18px', 
                    height: '18px',
                    accentColor: '#7c3aed'
                  }}
                  data-testid="advanced-search-toggle"
                />
                Advanced Search
              </label>
            </div>
            {advancedSearchEnabled && (
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                Enhanced lead discovery
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
          <h2 className="card-title">
            {advancedSearchEnabled ? "Advanced Search" : "Search Filters"}
          </h2>

          <div className="btn-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <Button
              onClick={handleClearFilters}
              className="btn btn-secondary"
              data-testid="clear-filters-btn"
              style={{ flex: '1', minWidth: '120px' }}
            >
              Clear Filters
            </Button>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="btn btn-primary"
                data-testid="search-btn"
                style={{ 
                  flex: '1', 
                  minWidth: '120px',
                  background: advancedSearchEnabled 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' 
                    : undefined
                }}
              >
                {loading ? "Searching..." : advancedSearchEnabled ? "Search Leads" : "Search Companies"}
              </Button>
          </div>
        </div>

        {/* Standard Filters (Hunter.io) */}
        {!advancedSearchEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger data-testid="industry-select">
                  <SelectValue placeholder="Select industry..." />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="form-label">Headcount</label>
              <Select value={selectedHeadcount} onValueChange={setSelectedHeadcount}>
                <SelectTrigger data-testid="headcount-select">
                  <SelectValue placeholder="Select headcount..." />
                </SelectTrigger>
                <SelectContent>
                  {headcountOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option} employees
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="form-label">Company Type</label>
              <Select value={selectedCompanyType} onValueChange={setSelectedCompanyType}>
                <SelectTrigger data-testid="company-type-select">
                  <SelectValue placeholder="Select company type..." />
                </SelectTrigger>
                <SelectContent>
                  {companyTypeOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Advanced Filters (Apollo.io) */}
        {advancedSearchEnabled && (
          <div>
            {/* Row 1: Job Title, Seniority, Industry */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Job Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={apolloJobTitle}
                  onChange={(e) => setApolloJobTitle(e.target.value)}
                  placeholder="e.g., Marketing Director"
                  data-testid="apollo-job-title-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Seniority Level</label>
                <Select value={apolloSeniority} onValueChange={setApolloSeniority}>
                  <SelectTrigger data-testid="apollo-seniority-select">
                    <SelectValue placeholder="Select seniority..." />
                  </SelectTrigger>
                  <SelectContent>
                    {apolloFilters.seniorities.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <label className="form-label">Industry</label>
                <Select value={apolloIndustry} onValueChange={setApolloIndustry}>
                  <SelectTrigger data-testid="apollo-industry-select">
                    <SelectValue placeholder="Select industry..." />
                  </SelectTrigger>
                  <SelectContent>
                    {apolloFilters.industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Person Location, Company Location, Keywords */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <LocationAutocomplete
                value={apolloPersonLocation}
                onChange={setApolloPersonLocation}
                label="Person Location"
                placeholder="Start typing... e.g., New York"
                helperText="Where the person lives"
                testId="apollo-person-location-input"
              />

              <LocationAutocomplete
                value={apolloCompanyLocation}
                onChange={setApolloCompanyLocation}
                label="Company Location"
                placeholder="Start typing... e.g., California"
                helperText="Company HQ location"
                testId="apollo-company-location-input"
              />

              <div className="form-group">
                <label className="form-label">Keywords</label>
                <input
                  type="text"
                  className="form-input"
                  value={apolloKeywords}
                  onChange={(e) => setApolloKeywords(e.target.value)}
                  placeholder="e.g., SaaS, B2B"
                  data-testid="apollo-keywords-input"
                />
              </div>
            </div>

            {/* Row 3: Employee Count and Audience Name */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Min Employees</label>
                <input
                  type="number"
                  className="form-input"
                  value={apolloEmployeeMin}
                  onChange={(e) => setApolloEmployeeMin(e.target.value)}
                  placeholder="e.g., 50"
                  min="1"
                  data-testid="apollo-employee-min-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Max Employees</label>
                <input
                  type="number"
                  className="form-input"
                  value={apolloEmployeeMax}
                  onChange={(e) => setApolloEmployeeMax(e.target.value)}
                  placeholder="e.g., 500"
                  min="1"
                  data-testid="apollo-employee-max-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Audience Name (for scan)</label>
                <input
                  type="text"
                  className="form-input"
                  value={apolloAudienceName}
                  onChange={(e) => setApolloAudienceName(e.target.value)}
                  placeholder="e.g., TechLeads"
                  data-testid="apollo-audience-name-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Location Filter Section (for standard Hunter.io search) */}
        {!advancedSearchEnabled && (
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <input
                type="checkbox"
                id="use-location"
                checked={useLocation}
                onChange={(e) => setUseLocation(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
                data-testid="use-location-checkbox"
              />
            <label htmlFor="use-location" style={{ fontSize: '14px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
              Filter by Location
            </label>
          </div>
          
          {useLocation && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">City *</label>
                <input
                  type="text"
                  className="form-input"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  placeholder="Enter city name"
                  data-testid="city-input"
                />
              </div>

              {!advancedSearchEnabled && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Country</label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger data-testid="country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">State {selectedCountry === "US" && "*"}</label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select state..." />
                  </SelectTrigger>
                  <SelectContent>
                    {usStates.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Standard Results Section (Hunter.io) */}
      {!advancedSearchEnabled && results.length > 0 && (
        <div className="content-card">
          <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
            <div>
              <h2 className="card-title">Search Results ({results.length} companies)</h2>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                {selectedCompanies.length} selected
              </p>
            </div>
            <div className="btn-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Button
                onClick={handleSelectAll}
                className="btn btn-secondary"
                data-testid="select-all-btn"
                style={{ flex: '1', minWidth: '100px' }}
              >
                {selectedCompanies.length === results.length ? "Deselect All" : "Select All"}
              </Button>
              <Button
                onClick={handleStartScan}
                disabled={scanning || selectedCompanies.length === 0}
                className="btn btn-primary"
                data-testid="start-scan-btn"
                style={{ flex: '1', minWidth: '120px' }}
              >
                {scanning ? "Starting..." : `Start Scan (${selectedCompanies.length})`}
              </Button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="data-table" data-testid="results-table" style={{ minWidth: '500px' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCompanies.length === results.length && results.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Company</th>
                  <th>Domain</th>
                  <th>Emails</th>
                </tr>
              </thead>
              <tbody>
                {results.map((company, index) => (
                  <tr key={company.domain || index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCompanies.includes(company.domain)}
                        onChange={() => handleSelectCompany(company.domain)}
                      />
                    </td>
                    <td>
                      <strong style={{ fontSize: '13px' }}>{company.organization || company.domain}</strong>
                    </td>
                    <td>
                      <a
                        href={`https://${company.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', textDecoration: 'none', fontSize: '13px' }}
                      >
                        {company.domain}
                      </a>
                    </td>
                    <td>
                      <span style={{ 
                        padding: '2px 8px', 
                        background: (typeof company.emails_count === 'object' ? company.emails_count?.total : company.emails_count) > 0 ? '#d1fae5' : '#f1f5f9', 
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: (typeof company.emails_count === 'object' ? company.emails_count?.total : company.emails_count) > 0 ? '#047857' : '#64748b'
                      }}>
                        {typeof company.emails_count === 'object' ? company.emails_count?.total || 0 : company.emails_count || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Advanced Results Section (Apollo.io) */}
      {advancedSearchEnabled && apolloResults.length > 0 && (
        <div className="content-card">
          <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
            <div>
              <h2 className="card-title" style={{ color: '#7c3aed' }}>
                Advanced Search Results
              </h2>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                Showing {apolloResults.length} of {apolloTotalRecords} total leads • Page {apolloCurrentPage} of {apolloTotalPages} • {selectedLeads.length} selected
              </p>
              {/* Availability stats - like Hunter.io */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '4px 12px',
                  background: '#d1fae5',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#047857'
                }}>
                  <span>📧</span>
                  <span>{emailsCount} with email</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '4px 12px',
                  background: '#dbeafe',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#1d4ed8'
                }}>
                  <span>📞</span>
                  <span>{phonesCount} with phone</span>
                </div>
              </div>
            </div>
            <div className="btn-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Button
                onClick={handleSelectAllLeads}
                className="btn btn-secondary"
                data-testid="select-all-leads-btn"
                style={{ flex: '1', minWidth: '100px' }}
              >
                {selectedLeads.length === apolloResults.length ? "Deselect All" : "Select All (Page)"}
              </Button>
              <Button
                onClick={handleEnrichSelected}
                disabled={enriching || selectedLeads.length === 0 || selectedLeads.length > 10}
                className="btn btn-secondary"
                data-testid="enrich-emails-btn"
                style={{ 
                  flex: '1', 
                  minWidth: '120px',
                  background: selectedLeads.length > 0 && selectedLeads.length <= 10 ? '#059669' : undefined,
                  color: selectedLeads.length > 0 && selectedLeads.length <= 10 ? 'white' : undefined
                }}
              >
                {enriching ? "Enriching..." : `Emails (1cr)`}
              </Button>
              <Button
                onClick={handleEnrichPhones}
                disabled={enrichingPhones || selectedLeads.length === 0 || selectedLeads.length > 10}
                className="btn btn-secondary"
                data-testid="enrich-phones-btn"
                style={{ 
                  flex: '1', 
                  minWidth: '120px',
                  background: selectedLeads.length > 0 && selectedLeads.length <= 10 ? '#0284c7' : undefined,
                  color: selectedLeads.length > 0 && selectedLeads.length <= 10 ? 'white' : undefined
                }}
              >
                {enrichingPhones ? "Requesting..." : `Phones (8cr)`}
              </Button>
              {/* Show Refresh button with countdown if there are pending phone enrichments */}
              {apolloResults.some(r => r._phone_pending) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    onClick={handleRefreshPhones}
                    disabled={refreshingPhones}
                    className="btn btn-secondary"
                    data-testid="refresh-phones-btn"
                    style={{ 
                      minWidth: '140px',
                      background: '#f59e0b',
                      color: 'white'
                    }}
                  >
                    {refreshingPhones ? "Checking..." : phoneRefreshCountdown > 0 
                      ? `⏱️ Auto-check in ${phoneRefreshCountdown}s` 
                      : "🔄 Refresh Phones"}
                  </Button>
                  {phoneRefreshCountdown > 0 && (
                    <span style={{ 
                      fontSize: '11px', 
                      color: '#64748b',
                      whiteSpace: 'nowrap'
                    }}>
                      Attempt {phoneRefreshAttempts + 1}/{maxRefreshAttempts}
                    </span>
                  )}
                </div>
              )}
              <Button
                onClick={handleStartScan}
                disabled={scanning || selectedLeads.length === 0}
                className="btn btn-primary"
                data-testid="start-advanced-scan-btn"
                style={{ 
                  flex: '1', 
                  minWidth: '120px',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                }}
              >
                {scanning ? "Starting..." : `Scan (${selectedLeads.length})`}
              </Button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="data-table" data-testid="apollo-results-table" style={{ minWidth: '850px' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedLeads.length === apolloResults.length && apolloResults.length > 0}
                      onChange={handleSelectAllLeads}
                    />
                  </th>
                  <th>Name</th>
                  <th>Fit</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Job Title</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apolloResults.map((lead, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(index)}
                        onChange={() => handleSelectLead(index)}
                        data-testid={`select-lead-${index}`}
                      />
                    </td>
                    <td>
                      <strong style={{ fontSize: '13px' }}>
                        {lead.first_name} {lead.last_name}
                      </strong>
                      {lead.seniority && (
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{lead.seniority}</div>
                      )}
                      {lead._enriched && (
                        <span style={{ 
                          marginLeft: '4px',
                          padding: '1px 6px', 
                          borderRadius: '8px',
                          fontSize: '9px',
                          fontWeight: '700',
                          background: '#d1fae5',
                          color: '#047857'
                        }}>
                          ENRICHED
                        </span>
                      )}
                    </td>
                    <td>
                      {lead.tier ? (
                        <TierBadge tier={lead.tier} score={lead.score} reasons={lead.score_reasons} />
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td>
                      <div>
                        <span style={{ fontSize: '13px' }}>{lead.company || '-'}</span>
                        {lead.company_domain && (
                          <div>
                            <a
                              href={`https://${lead.company_domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2563eb', textDecoration: 'none', fontSize: '11px' }}
                            >
                              {lead.company_domain}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {lead.email ? (
                        <div>
                          <a href={`mailto:${lead.email}`} style={{ color: '#2563eb', textDecoration: 'none', fontSize: '12px' }}>
                            {lead.email}
                          </a>
                          {lead.email_status && (
                            <span style={{ 
                              marginLeft: '6px',
                              padding: '2px 6px', 
                              borderRadius: '8px',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: lead.email_status === 'verified' ? '#d1fae5' : '#fef3c7',
                              color: lead.email_status === 'verified' ? '#047857' : '#b45309'
                            }}>
                              {lead.email_status}
                            </span>
                          )}
                          {getAllEmails(lead).length > 1 && (
                            <button
                              onClick={() => handleViewMore(lead)}
                              data-testid={`email-more-${index}`}
                              style={{
                                display: 'block',
                                marginTop: '2px',
                                padding: '1px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '600',
                                background: '#d1fae5',
                                color: '#047857',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              title="View all emails"
                            >
                              +{getAllEmails(lead).length - 1} more
                            </button>
                          )}
                        </div>
                      ) : lead._email_pending ? (
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#fef3c7',
                          color: '#b45309',
                          cursor: 'default'
                        }}
                        title="Email enrichment in progress..."
                        >
                          ⏳ Pending
                        </span>
                      ) : lead.has_email ? (
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#d1fae5',
                          color: '#047857',
                          cursor: 'default'
                        }}
                        title="Email available - select and click 'Emails (1cr)' to reveal"
                        >
                          📧 Available
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const phones = getAllPhones(lead);
                        if (phones.length > 0) {
                          return (
                            <div data-testid={`phone-cell-${index}`}>
                              <a href={`tel:${phones[0].number}`} style={{ color: '#2563eb', textDecoration: 'none', fontSize: '12px' }}>
                                {phones[0].number}
                              </a>
                              {phones[0].type && (
                                <span style={{ marginLeft: '6px', fontSize: '10px', color: '#64748b' }}>
                                  ({phones[0].type})
                                </span>
                              )}
                              {phones.length > 1 && (
                                <button
                                  onClick={() => handleViewMore(lead)}
                                  data-testid={`phone-more-${index}`}
                                  style={{
                                    display: 'block',
                                    marginTop: '2px',
                                    padding: '1px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    background: '#dbeafe',
                                    color: '#1d4ed8',
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                  title="View all phone numbers"
                                >
                                  +{phones.length - 1} more
                                </button>
                              )}
                            </div>
                          );
                        }
                        if (lead._phone_pending) {
                          return (
                            <span style={{ 
                              padding: '2px 8px', 
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: '#fef3c7',
                              color: '#b45309',
                              cursor: 'default'
                            }}
                            title="Phone enrichment in progress..."
                            >
                              ⏳ Pending
                            </span>
                          );
                        }
                        if (lead.has_phone) {
                          return (
                            <span style={{ 
                              padding: '2px 8px', 
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: '#dbeafe',
                              color: '#1d4ed8',
                              cursor: 'default'
                            }}
                            title="Phone available - select and click 'Phones (8cr)' to reveal"
                            >
                              📞 Available
                            </span>
                          );
                        }
                        return <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>;
                      })()}
                    </td>
                    <td style={{ fontSize: '12px' }}>{lead.job_title || '-'}</td>
                    <td style={{ fontSize: '12px' }}>
                      {[lead.city, lead.state, lead.country].filter(Boolean).join(', ') || '-'}
                    </td>
                    <td>
                      <Button
                        onClick={() => handleViewMore(lead)}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                        data-testid={`view-more-${index}`}
                      >
                        View More
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {apolloTotalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '16px',
              padding: '20px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}>
              {/* Page Size Selector */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Leads per page:</span>
                  <Select value={String(apolloPageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger data-testid="page-size-select" style={{ width: '80px', height: '32px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  {((apolloCurrentPage - 1) * apolloPageSize) + 1}-{Math.min(apolloCurrentPage * apolloPageSize, apolloTotalRecords)} of {apolloTotalRecords}
                </span>
              </div>

              {/* Page Navigation */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <Button
                  onClick={() => handlePageChange(1)}
                  disabled={apolloCurrentPage === 1 || loading}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', minWidth: '60px' }}
                  data-testid="first-page-btn"
                >
                  First
                </Button>
                <Button
                  onClick={() => handlePageChange(apolloCurrentPage - 1)}
                  disabled={apolloCurrentPage === 1 || loading}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', minWidth: '80px' }}
                  data-testid="prev-page-btn"
                >
                  ← Previous
                </Button>
                
                {/* Page Numbers */}
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, apolloCurrentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(apolloTotalPages, startPage + maxVisiblePages - 1);
                    
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    if (startPage > 1) {
                      pages.push(
                        <span key="start-ellipsis" style={{ padding: '6px 4px', color: '#64748b' }}>...</span>
                      );
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          disabled={loading}
                          className={i === apolloCurrentPage ? "btn btn-primary" : "btn btn-secondary"}
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px', 
                            minWidth: '36px',
                            background: i === apolloCurrentPage 
                              ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' 
                              : undefined,
                            color: i === apolloCurrentPage ? 'white' : undefined
                          }}
                          data-testid={`page-${i}-btn`}
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    if (endPage < apolloTotalPages) {
                      pages.push(
                        <span key="end-ellipsis" style={{ padding: '6px 4px', color: '#64748b' }}>...</span>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <Button
                  onClick={() => handlePageChange(apolloCurrentPage + 1)}
                  disabled={apolloCurrentPage === apolloTotalPages || loading}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', minWidth: '80px' }}
                  data-testid="next-page-btn"
                >
                  Next →
                </Button>
                <Button
                  onClick={() => handlePageChange(apolloTotalPages)}
                  disabled={apolloCurrentPage === apolloTotalPages || loading}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', minWidth: '60px' }}
                  data-testid="last-page-btn"
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced Scan: phone enrichment prompt */}
      <Dialog open={showPhonePrompt} onOpenChange={setShowPhonePrompt}>
        <DialogContent className="max-w-md" data-testid="phone-prompt-modal">
          <DialogHeader>
            <DialogTitle>Also reveal phone numbers?</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '8px 0' }}>
            <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
              Scanning will enrich <strong>emails</strong> and add these contacts to your CRM.
              Would you also like to reveal their <strong>phone numbers</strong>?
            </p>
            <div style={{
              marginTop: '12px', padding: '10px 12px', borderRadius: '8px',
              background: '#fef3c7', color: '#92400e', fontSize: '13px'
            }} data-testid="phone-prompt-credit-note">
              ⚠️ Credits are used automatically when scanning — emails cost 1 credit each,
              and phone numbers cost 8 credits each.
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px',
              fontSize: '13px', color: '#334155', cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={autoPhonesAlways}
                onChange={(e) => setAutoPhonesAlways(e.target.checked)}
                data-testid="phone-prompt-dont-ask"
              />
              Don't ask again — always add phone numbers automatically
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <Button
              onClick={() => runAdvancedScan(false)}
              className="btn btn-secondary"
              data-testid="phone-prompt-emails-only"
            >
              Emails only
            </Button>
            <Button
              onClick={() => {
                if (autoPhonesAlways) localStorage.setItem('revo_auto_enrich_phones', 'true');
                runAdvancedScan(true);
              }}
              className="btn btn-primary"
              data-testid="phone-prompt-add-phones"
            >
              Add phone numbers
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Details Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="max-w-2xl" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          
          {selectedLeadDetails && (() => {
            const lead = apolloResults.find(l => l?._raw?.id === selectedLeadDetails?._raw?.id) || selectedLeadDetails;
            const emails = getAllEmails(lead);
            const phones = getAllPhones(lead);
            const labelStyle = { color: '#64748b' };
            const rowStyle = { display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '14px' };
            return (
            <div style={{ padding: '16px 0' }} data-testid="lead-details-modal">
              <div style={{ marginBottom: '20px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                {lead.photo_url && (
                  <img
                    src={lead.photo_url}
                    alt={lead.first_name}
                    style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                    {lead.first_name} {lead.last_name}
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                    {lead.job_title || 'No job title'} 
                    {lead.company && ` at ${lead.company}`}
                  </p>
                  {lead.headline && (
                    <p style={{ color: '#475569', fontSize: '12px', marginTop: '6px', lineHeight: 1.4 }}>
                      {lead.headline}
                    </p>
                  )}
                </div>
              </div>

              {/* Get Complete Info action */}
              <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Button
                  onClick={() => handleGetCompleteInfo(lead)}
                  disabled={loadingCompleteInfo}
                  className="btn btn-primary"
                  data-testid="get-complete-info-btn"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {loadingCompleteInfo ? 'Fetching...' : (lead._complete_info ? '🔄 Refresh Complete Info' : '✨ Get Complete Info (1cr)')}
                </Button>
                {lead._complete_info && (
                  <span style={{ fontSize: '11px', color: '#047857', fontWeight: 600 }}>Complete profile loaded</span>
                )}
              </div>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                {/* Emails */}
                <div style={rowStyle}>
                  <span style={labelStyle}>Emails:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} data-testid="modal-emails">
                    {emails.length > 0 ? emails.map((em, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <a href={`mailto:${em.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{em.email}</a>
                        {em.type && (
                          <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: '#eef2ff', color: '#4338ca' }}>{em.type}</span>
                        )}
                        {em.status && (
                          <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: em.status === 'verified' ? '#d1fae5' : '#fef3c7', color: em.status === 'verified' ? '#047857' : '#b45309' }}>{em.status}</span>
                        )}
                      </div>
                    )) : (
                      <span style={{ color: '#94a3b8' }}>{lead.has_email ? 'Available - reveal via Emails (1cr)' : 'None'}</span>
                    )}
                  </div>
                </div>

                {/* Phone numbers */}
                <div style={rowStyle}>
                  <span style={labelStyle}>Phones:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} data-testid="modal-phones">
                    {phones.length > 0 ? phones.map((ph, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <a href={`tel:${ph.number}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{ph.number}</a>
                        {ph.type && (
                          <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>{ph.type}</span>
                        )}
                        {ph.status && (
                          <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: ph.status === 'valid_number' ? '#d1fae5' : '#f1f5f9', color: ph.status === 'valid_number' ? '#047857' : '#64748b' }}>{ph.status}</span>
                        )}
                      </div>
                    )) : lead._phone_pending ? (
                      <span style={{ color: '#b45309' }}>⏳ Enrichment in progress...</span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>{lead.has_phone ? 'Available - reveal via Phones (8cr)' : 'None'}</span>
                    )}
                  </div>
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>Social:</span>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }} data-testid="modal-socials">
                    {lead.linkedin_url ? (
                      <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>LinkedIn</a>
                    ) : null}
                    {lead.twitter_url ? (
                      <a href={lead.twitter_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Twitter/X</a>
                    ) : null}
                    {lead.facebook_url ? (
                      <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Facebook</a>
                    ) : null}
                    {lead.github_url ? (
                      <a href={lead.github_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>GitHub</a>
                    ) : null}
                    {!lead.linkedin_url && !lead.twitter_url && !lead.facebook_url && !lead.github_url && (
                      <span style={{ color: '#94a3b8' }}>-</span>
                    )}
                  </div>
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>Location:</span>
                  <span>{lead.formatted_address || [lead.city, lead.state, lead.country].filter(Boolean).join(', ') || '-'}</span>
                </div>
                
                <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                
                <div style={rowStyle}>
                  <span style={labelStyle}>Company:</span>
                  <span>{lead.company || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Domain:</span>
                  <span>{lead.company_domain || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Industry:</span>
                  <span>{lead.company_industry || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Employees:</span>
                  <span>{lead.company_employee_count || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Revenue:</span>
                  <span>{lead.company_revenue || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Website:</span>
                  <span>
                    {lead.company_website ? (
                      <a href={lead.company_website} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{lead.company_website}</a>
                    ) : '-'}
                  </span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Founded:</span>
                  <span>{lead.company_founded_year || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Company Phone:</span>
                  <span>{lead.company_phone || '-'}</span>
                </div>
                
                <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                
                <div style={rowStyle}>
                  <span style={labelStyle}>Department:</span>
                  <span>{lead.department || '-'}</span>
                </div>
                <div style={rowStyle}>
                  <span style={labelStyle}>Seniority:</span>
                  <span>{lead.seniority || '-'}</span>
                </div>

                {/* Employment history (available after Get Complete Info) */}
                {lead.employment_history && lead.employment_history.length > 0 && (
                  <>
                    <hr style={{ margin: '8px 0', borderColor: '#e2e8f0' }} />
                    <div>
                      <span style={{ ...labelStyle, fontWeight: 600, display: 'block', marginBottom: '8px' }}>Employment History:</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="modal-employment-history">
                        {lead.employment_history.map((job, i) => (
                          <div key={i} style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: '10px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600 }}>
                              {job.title || 'Role'}
                              {job.current && (
                                <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: '#d1fae5', color: '#047857' }}>Current</span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{job.organization_name || '-'}</div>
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                              {(job.start_date || '').slice(0, 7) || '?'} — {job.current ? 'Present' : ((job.end_date || '').slice(0, 7) || '?')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Discover;
